import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, PaperPlaneTilt, CheckCircle, XCircle, PencilSimple, GitBranch, ClipboardText, Eye, FilePdf, Archive, ArrowCounterClockwise, Trash, Share, Copy, LinkSimpleBreak, Warning, ShieldCheck, Buildings, UserCircle, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { formatLocal, formatLocalDate } from "@/lib/timezone";
import RichTextEditor from "@/pages/marketing/RichTextEditor";
import { detectRecipientMismatch, joinName } from "@/lib/recipientMismatch";

const fmt = (n, c) => { try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: c || "USD" }).format(n); } catch { return `${c} ${n?.toFixed(2)}`; } };
// Backend errors ship a plain string `detail` (legacy routes) or a
// `{success, code, message, referenceId}` object (newer safe-error
// contract, e.g. PDF generation). Handle both so the user always sees
// the actual reason instead of a generic message.
const extractErrorDetail = (detail, fallback) => {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (typeof detail === "object") return detail.message || fallback;
  return fallback;
};
// Display gates use round2(x) !== 0 rather than x > 0 so a credit line
// (negative amount) still renders instead of silently vanishing.
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const lineSub = (l) => ((Number(l.quantity) || 0) * (Number(l.unit_price) || 0)) - (Number(l.line_discount_amount) || 0);
// Session 91 — Services lines can carry an optional per-row ongoing
// monthly fee on top of the principal. Per Mike's spec the add-on is
// ALWAYS × 1 (it's an ongoing per-line commitment, not a per-unit
// charge) — so qty does NOT multiply it. Feeds the section + grand
// totals as "recurring fees".
const lineAddonMo = (l) => Number(l.monthly_addon_unit_price) || 0;
const lineMo = (l) => { const pt = l.pricing_type_snapshot || "annual"; const s = lineSub(l); const base = pt === "monthly" ? s : pt === "annual" ? s / 12 : 0; return base + lineAddonMo(l); };
const lineYr = (l) => { const pt = l.pricing_type_snapshot || "annual"; const s = lineSub(l); const base = pt === "annual" ? s : pt === "monthly" ? s * 12 : 0; return base + (lineAddonMo(l) * 12); };
const lineOO = (l) => (l.pricing_type_snapshot || "annual") === "one_off" ? lineSub(l) : 0;

const STATUS_LABEL = { draft: "Draft", in_rfp: "In RFP", sent: "Sent", returned: "Returned", accepted: "Accepted", rejected: "Rejected", expired: "Expired" };
const STATUS_COLORS = { draft: "bg-[#F5F5F5] text-[#525252]", in_rfp: "bg-purple-50 text-purple-700", sent: "bg-blue-50 text-blue-700", returned: "bg-[#F0F7FF] text-[#1E3A8A]", accepted: "bg-emerald-50 text-emerald-700", rejected: "bg-red-50 text-red-700", expired: "bg-amber-50 text-amber-700" };
const SECTIONS = [
  { key: "user_licenses", label: "User Licenses", headerCls: "bg-blue-50 text-blue-900 border-b border-blue-200" },
  { key: "optional_modules", label: "Optional Modules", headerCls: "bg-purple-50 text-purple-900 border-b border-purple-200" },
  { key: "implementation_fees", label: "Implementation Fees", headerCls: "bg-green-50 text-green-900 border-b border-green-200" },
  { key: "services", label: "Services", headerCls: "bg-amber-50 text-amber-900 border-b border-amber-200" },
];

export default function QuoteDetail() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [emailLog, setEmailLog] = useState([]);
  const [versions, setVersions] = useState([]);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionNote, setRevisionNote] = useState("");
  const [creatingRevision, setCreatingRevision] = useState(false);
  // Send-confirmation dialog — lets the user review (and optionally override)
  // the recipient email right before the proposal leaves, so the default
  // from the lead/client record can be adjusted without re-editing the quote.
  const [sendConfirm, setSendConfirm] = useState(null); // null | {to, original, type}
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [sendSlowNotice, setSendSlowNotice] = useState(false);
  const [shareDialog, setShareDialog] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [autoEmailOnApproval, setAutoEmailOnApproval] = useState(true);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  // Session 88 — Accept-on-behalf + Send-follow-up dialogs.
  const [showAcceptOnBehalf, setShowAcceptOnBehalf] = useState(false);
  const [aobEmail, setAobEmail] = useState("");
  const [aobName, setAobName] = useState("");
  const [aobEvidence, setAobEvidence] = useState("");
  // Mirror state for "Mark Rejected (Email)" — separate dialog so the
  // two flows can't be conflated and so the evidence textareas don't
  // bleed between accept/reject sessions.
  const [showRejectOnBehalf, setShowRejectOnBehalf] = useState(false);
  const [robEmail, setRobEmail] = useState("");
  const [robName, setRobName] = useState("");
  const [robEvidence, setRobEvidence] = useState("");
  const [showFollowup, setShowFollowup] = useState(false);
  const [fuLoading, setFuLoading] = useState(false);
  const [fuSubject, setFuSubject] = useState("");
  const [fuBodyHtml, setFuBodyHtml] = useState("");
  const [fuTo, setFuTo] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState(null);
  const [converting, setConverting] = useState(false);
  // ── "Return With Comments" thread ────────────────────────────────
  const [threads, setThreads] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [replyFile, setReplyFile] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);
  const [applyingRecommended, setApplyingRecommended] = useState(false);

  // ── Recipient resolution ──────────────────────────────────────────
  // fix/proposal-recipient-contact-sync — surfaces who an Email-to-Client
  // action will ACTUALLY go to, straight from the quote's OWN stored
  // recipient_first_name/last_name + billing_email. A Greet-As chip now
  // always writes both atomically, so the stored pair IS the source of
  // truth: this no longer recomputes a separate PM→Billing→Primary
  // "recommended" pair and prefers it — that used to silently override an
  // intentionally-selected Billing/Primary contact with the PM's own
  // email (Mike's report: greeting said "Theresa", the To: address was
  // Lorna's). Mismatch detection is delegated to the shared, unit-tested
  // `detectRecipientMismatch` (src/lib/recipientMismatch.js), which
  // mirrors the backend's set-based `_detect_recipient_mismatch` exactly
  // — see that module for why a first-match name lookup is unsafe when
  // two roles (e.g. Billing and Primary) share the same displayed name.
  const resolvedRecipient = useMemo(() => {
    if (!data) return null;
    const q = data.quote || {};
    const c = data.client || {};

    const savedEmail = (q.billing_email || "").trim();
    const savedName  = joinName(q.recipient_first_name, q.recipient_last_name);
    const effectiveEmail = savedEmail;
    const effectiveName  = savedName || q.recipient_name || "";
    const mismatch = detectRecipientMismatch(q, c);

    return { effectiveEmail, effectiveName, savedEmail, savedName, mismatch };
  }, [data]);

  const applyRecommendedContact = async () => {
    const mismatch = resolvedRecipient && resolvedRecipient.mismatch;
    // Never offer (or act on) a one-click fix when the greeted name is
    // ambiguous across more than one role — we can't know which of the
    // same-named contacts' emails the operator actually intended. The
    // operator must reselect via the Greet-As chips instead.
    if (!mismatch || mismatch.ambiguous || !mismatch.fixRole) return;
    setApplyingRecommended(true);
    try {
      await api.put(`/quotes/${quoteId}`, {
        billing_email: mismatch.fixEmail,
        recipient_contact_role: mismatch.fixRole,
      });
      toast.success(`Recipient email corrected to ${mismatch.greetedName}'s own ${mismatch.greetedSources[0]} address.`);
      load();
    } catch (e) {
      toast.error(extractErrorDetail(e?.response?.data?.detail, "Failed to update recipient."));
    } finally {
      setApplyingRecommended(false);
    }
  };

  const load = () => {
    api.get(`/quotes/${quoteId}`).then(r => setData(r.data)).finally(() => setLoading(false));
    api.get(`/quotes/${quoteId}/email-log`).then(r => setEmailLog(r.data)).catch(() => {});
    api.get(`/quotes/${quoteId}/versions`).then(r => setVersions(r.data)).catch(() => {});
    api.get(`/quotes/${quoteId}/share-link`).then(r => setShareInfo(r.data.active ? r.data : null)).catch(() => {});
    api.get(`/billing/quotes/${quoteId}/invoice-status`).then(r => setInvoiceStatus(r.data)).catch(() => {});
    api.get(`/quotes/${quoteId}/comment-threads`).then(r => setThreads(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, [quoteId]);

  const openShareDialog = async () => {
    setShareDialog(true);
    if (!shareInfo) {
      setShareLoading(true);
      try {
        const res = await api.post(`/quotes/${quoteId}/share-link`);
        setShareInfo({ active: true, share_token: res.data.share_token, url: res.data.url, view_count: 0 });
      } catch { toast.error("Failed to create share link"); }
      setShareLoading(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareInfo?.url) return;
    try {
      await navigator.clipboard.writeText(shareInfo.url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const revokeShareLink = async () => {
    if (!window.confirm("Revoke this share link? Anyone with the URL will lose access.")) return;
    setShareLoading(true);
    try {
      await api.post(`/quotes/${quoteId}/share-link/revoke`);
      setShareInfo(null);
      toast.success("Share link revoked");
      setShareDialog(false);
    } catch { toast.error("Failed to revoke"); }
    setShareLoading(false);
  };

  const action = async (type) => { setActionLoading(type); try { await api.post(`/quotes/${quoteId}/${type}`); load(); } catch (e) { alert(e.response?.data?.detail || "Failed"); } setActionLoading(""); };

  // Session 88 — Operator-side accept for customers who reply by email.
  const openAcceptOnBehalf = () => {
    setAobEmail(quote?.billing_email || "");
    setAobName("");
    setAobEvidence("");
    setShowAcceptOnBehalf(true);
  };
  const submitAcceptOnBehalf = async () => {
    if (!aobEmail.trim() || !aobEmail.includes("@")) { toast.error("A valid client contact email is required"); return; }
    if (!aobEvidence.trim()) { toast.error("Paste the customer's email body or a brief note as evidence"); return; }
    setActionLoading("accept-on-behalf");
    try {
      await api.post(`/quotes/${quoteId}/accept-on-behalf`, {
        accepted_by_email: aobEmail.trim(),
        accepted_by_name: aobName.trim() || null,
        evidence_text: aobEvidence.trim(),
      });
      toast.success(`Marked accepted on behalf of ${aobEmail.trim()}`);
      setShowAcceptOnBehalf(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Accept-on-behalf failed");
    }
    setActionLoading("");
  };

  const openRejectOnBehalf = () => {
    setRobEmail(quote?.billing_email || "");
    setRobName("");
    setRobEvidence("");
    setShowRejectOnBehalf(true);
  };
  const submitRejectOnBehalf = async () => {
    if (!robEmail.trim() || !robEmail.includes("@")) { toast.error("A valid client contact email is required"); return; }
    if (!robEvidence.trim()) { toast.error("Paste the customer's email body or a brief note as evidence"); return; }
    setActionLoading("reject-on-behalf");
    try {
      await api.post(`/quotes/${quoteId}/reject-on-behalf`, {
        rejected_by_email: robEmail.trim(),
        rejected_by_name: robName.trim() || null,
        evidence_text: robEvidence.trim(),
      });
      toast.success(`Marked rejected on behalf of ${robEmail.trim()}`);
      setShowRejectOnBehalf(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject-on-behalf failed");
    }
    setActionLoading("");
  };

  // Session 88 — Send a manual follow-up email. Pre-fills from the
  // system-admin template with merge-fields already resolved.
  const openFollowup = async () => {
    setFuLoading(true);
    try {
      const r = await api.get(`/quotes/${quoteId}/followup-template`);
      setFuSubject(r.data.subject || "");
      setFuBodyHtml(r.data.body_html || "");
      setFuTo(r.data.to_email || quote?.billing_email || "");
      setShowFollowup(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load template");
    }
    setFuLoading(false);
  };
  const submitFollowup = async () => {
    if (!fuTo.trim() || !fuTo.includes("@")) { toast.error("A valid recipient email is required"); return; }
    if (!fuSubject.trim()) { toast.error("Subject is required"); return; }
    if (!fuBodyHtml.trim()) { toast.error("Email body is required"); return; }
    setActionLoading("send-followup");
    try {
      const r = await api.post(`/quotes/${quoteId}/send-followup`, {
        subject: fuSubject.trim(),
        body_html: fuBodyHtml,
        to_email: fuTo.trim(),
      });
      toast.success(`Follow-up sent to ${r.data.to_email}`);
      setShowFollowup(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setActionLoading("");
  };

  // Manually fire a reminder to the proposal recipient. Reads the
  // CURRENT billing_email so it always goes to the right person —
  // protects against the historical Kinterra-style situation where the
  // original send went to a stale test address. The audit log captures
  // the manual trigger and the next auto-reminder advances by 7 days.
  const sendReminderNow = async () => {
    const to = quote?.billing_email || "(no recipient set)";
    if (!window.confirm(
      `Send a manual reminder for proposal ${quote.quote_number} now?\n\n` +
      `Recipient: ${to}\n\n` +
      `This counts as one of the 9 reminder cycles for this proposal. ` +
      `The next automatic reminder will then fire 7 days from now.`
    )) return;
    setActionLoading("send-reminder-now");
    try {
      const r = await api.post(`/quotes/${quoteId}/send-reminder-now`);
      toast.success(`Reminder #${r.data.reminder_number} sent to ${r.data.to}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Manual reminder failed");
    }
    setActionLoading("");
  };

  // Toggle whether THIS proposal requires DocuSign signature on accept.
  // (Session 1.15 — DocuSign integration.)
  const toggleDocusign = async () => {
    const turningOn = !quote.use_docusign;
    if (turningOn && !window.confirm(
      `Require DocuSign signature for proposal ${quote.quote_number}?\n\n` +
      `When the customer clicks Accept, they'll receive a DocuSign envelope ` +
      `at ${quote.billing_email || "(no recipient)"} for legally-binding ` +
      `signature. The proposal will only be marked Accepted after they sign.`
    )) return;
    setActionLoading("toggle-docusign");
    try {
      await api.patch(`/quotes/${quoteId}/use-docusign`, { use_docusign: turningOn });
      toast.success(turningOn ? "DocuSign required on accept" : "DocuSign disabled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
    setActionLoading("");
  };

  // Admin-only smoke test — sends a real DocuSign envelope using THIS
  // quote's PDF, but addressed to the current operator (not the
  // customer). Use to verify the integration works without involving
  // a real customer.
  const sendDocusignTestEnvelope = async () => {
    if (!window.confirm(
      `Send a test DocuSign envelope using proposal ${quote.quote_number}?\n\n` +
      `The envelope will be sent to YOUR email (not the customer's). ` +
      `It will use the real proposal PDF, but no quote status changes ` +
      `will occur when you sign or decline.`
    )) return;
    setActionLoading("docusign-test");
    try {
      const r = await api.post(`/quotes/${quoteId}/docusign-test-envelope`);
      toast.success(`Test envelope sent to ${r.data.sent_to} (id: ${r.data.envelope_id?.slice(0, 8)}…)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test envelope failed");
    }
    setActionLoading("");
  };

  // Flip the proposal between 'new_customer' and 'adhoc'. Allowed at any
  // status — if the proposal has already been sent/accepted/rejected we
  // confirm first so a click doesn't silently rewrite the PDF shape on
  // the client's next view.
  const switchQuoteType = async (nextType) => {
    const q = data?.quote;
    if (!q) return;
    const curType = q.quote_type || "new_customer";
    if (curType === nextType) return;
    const isActive = !["draft", "in_rfp"].includes(q.status);
    if (isActive) {
      const msg = nextType === "adhoc"
        ? `Switch ${q.quote_number} to Ad-hoc? This will drop the contract term, Year-1 total, and ongoing-annual breakdown from the PDF. The status (${q.status}) is unchanged.`
        : `Switch ${q.quote_number} to New Customer? This will restore the contract term, Year-1 total, and ongoing-annual breakdown in the PDF. The status (${q.status}) is unchanged.`;
      if (!window.confirm(msg)) return;
    }
    setActionLoading("switch-type");
    try {
      await api.patch(`/quotes/${quoteId}/quote-type`, { quote_type: nextType });
      toast.success(nextType === "adhoc" ? "Switched to Ad-hoc" : "Switched to New Customer");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to switch type"); }
    setActionLoading("");
  };

  // Open the recipient-confirmation dialog for `send`. Actual POST happens
  // in confirmSend() after the user reviews the To: address.
  // fix/proposal-recipient-contact-sync — the quote's OWN stored
  // billing_email + recipient_first/last_name is now always an
  // atomically-synced pair (a Greet-As chip writes both together), so
  // it is the source of truth for BOTH the To: address and the greeting
  // name. This must NEVER be replaced by a freshly-recomputed
  // PM→Billing→Primary chain — that was the exact cause of Mike's
  // Theresa/Lorna report: the dialog silently swapped in the PM's email
  // underneath an already-correct Billing/Primary name. The client's
  // contact chain is used ONLY as a convenience default when the quote
  // genuinely has no recipient email at all yet (e.g. a very old draft).
  const openSendConfirm = () => {
    const client = data?.client || {};
    const quote = data?.quote || {};
    const joinName = (first, last) =>
      [first, last].map(s => (s || "").trim()).filter(Boolean).join(" ");

    const storedEmail = (quote.billing_email || "").trim();
    const storedName  = joinName(quote.recipient_first_name, quote.recipient_last_name);

    let to = storedEmail;
    let name = storedName || quote.recipient_name || "";
    let source = to ? "proposal" : "";

    if (!to) {
      const pm = (client.pm_contact_email || "").trim();
      const billing = (client.billing_email || "").trim();
      const contact = (client.contact_email || "").trim();
      const pmName = joinName(client.pm_first_name, client.pm_last_name) ||
                     (client.pm_contact_name || "").trim();
      const billingName = joinName(client.billing_first_name, client.billing_last_name) ||
                          (client.billing_contact_name || "").trim();
      const contactName = joinName(client.contact_first_name, client.contact_last_name) ||
                          (client.contact_name || client.contact_person || "").trim();
      if (pm) { to = pm; name = name || pmName; source = "pm"; }
      else if (billing) { to = billing; name = name || billingName; source = "billing"; }
      else if (contact) { to = contact; name = name || contactName; source = "contact"; }
    }

    setSendConfirm({
      to,
      name,
      originalName: name,
      original: storedEmail,
      source,
      cc: "",
      type: quote.status === "sent" ? "resend" : "send",
    });
  };
  const confirmSend = async () => {
    if (!sendConfirm) return;
    const newTo = (sendConfirm.to || "").trim();
    if (!newTo || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newTo)) {
      toast.error("Enter a valid recipient email"); return;
    }
    const newName = (sendConfirm.name || "").trim();
    if (newName.length > 200) {
      toast.error("Recipient name is too long (max 200 chars)"); return;
    }
    // Parse CC list (comma / semicolon / whitespace separated).
    const ccList = (sendConfirm.cc || "")
      .split(/[,;\s]+/)
      .map(s => s.trim())
      .filter(Boolean);
    for (const addr of ccList) {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) {
        toast.error(`'${addr}' is not a valid CC email`); return;
      }
    }
    setActionLoading("send");
    try {
      // Schedule-for-later branch (Session 67) — applies recipient
      // overrides AND CCs to the quote first (so they're used at
      // dispatch time), then PATCHes scheduled_send_at.
      if (sendConfirm.type !== "resend" && sendConfirm.scheduleMode === "later") {
        const scheduledAt = sendConfirm.scheduledAt;
        if (!scheduledAt) { toast.error("Pick a send time"); setActionLoading(""); return; }
        const iso = new Date(scheduledAt).toISOString();
        // 1. Persist overrides on the quote so the dispatcher uses them.
        const updates = {};
        if (newTo.toLowerCase() !== (sendConfirm.original || "").toLowerCase()) {
          updates.billing_email = newTo;
        }
        if (newName && newName !== (sendConfirm.originalName || "")) {
          // Split into first/last for the salutation pipeline.
          const parts = newName.split(/\s+/);
          updates.recipient_first_name = parts[0] || "";
          updates.recipient_last_name = parts.slice(1).join(" ") || "";
        }
        if (Object.keys(updates).length) {
          await api.put(`/quotes/${quoteId}`, updates);
        }
        // 2. Schedule the dispatch.
        await api.patch(`/quotes/${quoteId}/scheduled-send`,
                        { scheduled_send_at: iso });
        toast.success(`Scheduled for ${new Date(scheduledAt).toLocaleString()}`);
        setSendConfirm(null);
        load();
        setActionLoading("");
        return;
      }

      const body = {};
      if (newTo.toLowerCase() !== (sendConfirm.original || "").toLowerCase()) {
        body.override_email = newTo;
      }
      if (newName && newName !== (sendConfirm.originalName || "")) {
        body.override_name = newName;
      } else if (newName) {
        // Always forward the confirmed name so greetings use the current
        // PM/billing/contact entry (not whatever stale contact_person the
        // quote record might still have).
        body.override_name = newName;
      }
      if (ccList.length) body.cc_emails = ccList;
      // The proposal send is synchronous end-to-end (HTML build + PDF
      // attachment + the actual SendGrid call all happen before this
      // resolves) and has been observed taking a while in production —
      // if it's still going after 10s, tell the user rather than leaving
      // them staring at a static "Sending proposal…" button.
      const slowTimer = setTimeout(() => setSendSlowNotice(true), 10_000);
      try {
        await api.post(`/quotes/${quoteId}/send`, body);
      } finally {
        clearTimeout(slowTimer);
        setSendSlowNotice(false);
      }
      // Only reported after the backend has confirmed SendGrid accepted the
      // message — never show success on a request that merely completed.
      toast.success(
        `${sendConfirm.type === "resend" ? "Resent" : "Sent"} to ${newName ? newName + " <" + newTo + ">" : newTo}` +
        (ccList.length ? ` (CC ${ccList.length})` : "")
      );
      setSendConfirm(null);
      load();
    } catch (e) {
      // The backend's recipient-mismatch guard (fix/proposal-recipient-
      // contact-sync) returns a structured {code, message} detail —
      // unwrap it so the operator sees the actual repair instruction
      // instead of a raw object.
      toast.error(extractErrorDetail(e.response?.data?.detail, "Send failed"));
    }
    setSendSlowNotice(false);
    setActionLoading("");
  };

  const submitForApproval = async () => {
    setActionLoading("submit-for-approval");
    try {
      await api.post(`/quotes/${quoteId}/submit-for-approval`, { auto_email_on_approval: autoEmailOnApproval });
      setShowSubmit(false);
      toast.success("Submitted for approval");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    setActionLoading("");
  };

  const approveProposal = async () => {
    if (!window.confirm(`Approve proposal ${data.quote.quote_number}?${data.quote.auto_email_on_approval ? " This will also email it to the client." : ""}`)) return;
    setActionLoading("approve");
    try {
      const r = await api.post(`/quotes/${quoteId}/approve`);
      toast.success(r.data.auto_sent ? "Approved and emailed to client" : "Approved — originator can now send");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to approve"); }
    setActionLoading("");
  };

  const rejectProposal = async () => {
    setActionLoading("reject-approval");
    try {
      await api.post(`/quotes/${quoteId}/reject-approval`, { reason: rejectReason });
      setShowReject(false);
      setRejectReason("");
      toast.success("Rejected — originator notified");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reject"); }
    setActionLoading("");
  };

  const createRevision = async () => {
    setCreatingRevision(true);
    try { const res = await api.post(`/quotes/${quoteId}/create-revision`, { version_note: revisionNote || undefined }); setShowRevisionModal(false); navigate(`/quotes/${res.data.quote_id}`); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
    setCreatingRevision(false);
  };

  const copyProposal = async () => {
    if (!window.confirm("Create a copy of this proposal as a new draft?")) return;
    try {
      const res = await api.post(`/quotes/${quoteId}/copy`);
      const { quote_number, line_items_copied, email_reset_note, billing_email } = res.data;
      if (email_reset_note) {
        toast.success(`Copied to ${quote_number} — ${line_items_copied} line${line_items_copied === 1 ? "" : "s"}. Recipient reset to ${billing_email}.`, { duration: 6000 });
      } else {
        toast.success(`Copied to new proposal ${quote_number} (${line_items_copied} line${line_items_copied === 1 ? "" : "s"})`);
      }
      navigate(`/quotes/${res.data.quote_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Copy failed"); }
  };

  const sendReply = async () => {
    const msg = replyText.trim();
    if (msg.length < 2) { toast.error("Please enter a reply message"); return; }
    setSendingReply(true);
    try {
      const fd = new FormData();
      fd.append("message", msg);
      if (replyFile) fd.append("attachment", replyFile);
      const r = await api.post(`/quotes/${quoteId}/reply-to-client`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(r.data.to ? `Reply sent to ${r.data.to}` : "Reply saved (no client email on file)");
      setReplyText("");
      setReplyFile(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reply");
    }
    setSendingReply(false);
  };

  const convertToInvoice = async () => {    if (!window.confirm(`Create a draft invoice in FinOps from ${quote?.quote_number}?\n\nThis will:\n  • Write a DRAFT invoice record in FinOps (not sent)\n  • Seed recurring items for future annual renewals\n  • Be reviewed/sent by the A/R team in FinOps`)) return;
    setConverting(true);
    try {
      const r = await api.post(`/billing/quotes/${quoteId}/convert-to-invoice`, {
        due_days: 30,
        include_one_off: true,
        include_recurring: true,
        seed_recurring_items: true,
      });
      toast.success(`Draft invoice ${r.data.invoice_number} created in FinOps (${r.data.recurring_items_seeded} recurring items seeded).`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Convert failed"); }
    setConverting(false);
  };

  if (loading) return <div className="p-6"><div className="h-4 bg-[#F5F5F5] animate-pulse w-48" /></div>;
  if (!data) return <div className="p-6 text-[#A3A3A3]">Proposal not found</div>;

  const { quote, line_items, client } = data;
  // Source of truth is the proposal's own `recipient_name` — that's
  // what the operator entered when sending. Linked client/lead is just
  // a relationship hint and can drift (or be wrong) without invalidating
  // the proposal. Falling back to client.legal_name / lead.company_name
  // ONLY when the proposal itself didn't carry a name.
  const recipientName = quote.recipient_name || client?.legal_name || "-";
  const isAdmin = hasRole("platform_admin");
  const isOriginator = user?.id && quote.created_by_user_id === user.id;
  const approvalState = quote.approval_state; // null | 'pending' | 'approved' | 'rejected'
  const pending = approvalState === "pending";
  const needsApproval = quote.status === "draft" && !pending && approvalState !== "approved";
  // Edit locked while pending unless admin is reviewing
  const canEdit = ["draft", "in_rfp"].includes(quote.status) && (!pending || isAdmin);
  const canRevise = ["sent", "in_rfp"].includes(quote.status);
  // Send button visibility: show only when not pending; non-admins can see but it'll be gated by backend.
  // Practical UX: hide Send entirely when non-admin + needsApproval+pending; show "Submit for Approval" instead.
  const canSendDirectly = approvalState === "approved" || isAdmin || quote.status === "sent";
  const totalMo = line_items.reduce((s, l) => s + lineMo(l), 0);
  const totalYr = line_items.reduce((s, l) => s + lineYr(l), 0);
  const totalOO = line_items.reduce((s, l) => s + lineOO(l), 0);
  const annualTotal = totalYr + totalOO - (quote.discount_amount || 0) + (quote.tax_amount || 0);

  const previewUrl = `${api.defaults.baseURL}/quotes/${quoteId}/preview`;

  // Open the preview. We mint a one-shot ticket (valid 5 min) rather than
  // embedding the long-lived JWT in the URL — the ticket is tied to this
  // user+quote and cannot be replayed elsewhere. We fetch the HTML
  // ourselves (rather than pointing an <iframe src> straight at the
  // backend host) so we can check the response before showing anything:
  // the frontend and backend are different origins in production, and an
  // <iframe src> pointed at a failed cross-origin response just renders
  // as a silent broken-document icon with no way for us to detect or
  // explain the failure. Rendering the fetched HTML via `srcDoc` also
  // sidesteps that origin entirely.
  const openPreview = async () => {
    setPreviewError("");
    setPreviewHtml("");
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      const r = await api.post(`/quotes/${quoteId}/preview-ticket`);
      const res = await fetch(`${previewUrl}?ticket=${encodeURIComponent(r.data.ticket)}`, {
        credentials: "include",
      });
      if (!res.ok) {
        let detail = null;
        try { detail = (await res.json())?.detail; } catch { /* non-JSON error body */ }
        setPreviewError(extractErrorDetail(detail, "Unable to load the proposal preview. Please try again. If it continues, contact support."));
        return;
      }
      setPreviewHtml(await res.text());
    } catch (e) {
      setPreviewError("Unable to load the proposal preview. Please check your connection and try again.");
    } finally {
      setPreviewLoading(false);
    }
  };
  const pdfUrl = `${api.defaults.baseURL}/quotes/${quoteId}/pdf`;

  const downloadPdf = async () => {
    if (pdfLoading) return;
    setPdfLoading(true);
    const toastId = toast.loading("Generating PDF… this may take up to a minute.");
    // WeasyPrint rendering has been observed taking close to a minute in
    // production — 100s gives it headroom before we tell the user to give up,
    // rather than leaving them staring at an indefinite spinner.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 100_000);
    try {
      // Session 43: cookie-based auth only. withCredentials attaches
      // the HttpOnly eyvo_auth cookie.
      const res = await fetch(pdfUrl, { credentials: "include", signal: controller.signal });
      if (!res.ok) {
        let detail = null;
        try { detail = (await res.json())?.detail; } catch { /* non-JSON error body */ }
        toast.error(extractErrorDetail(detail, "Unable to generate the proposal PDF. Please try again. If it continues, contact support."), { id: toastId });
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Eyvo_Proposal_${quote.quote_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Proposal PDF downloaded.", { id: toastId });
    } catch (e) {
      if (e?.name === "AbortError") {
        toast.error("PDF generation is taking longer than expected. Please try again in a moment — if it keeps happening, contact support.", { id: toastId });
      } else {
        toast.error("Unable to generate the proposal PDF. Please check your connection and try again.", { id: toastId });
      }
    } finally {
      clearTimeout(timeoutId);
      setPdfLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl" data-testid="quote-detail-page">
      {/* Preview dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-[#E5E5E5]">
            <DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Proposal Preview — {quote.quote_number}
            </DialogTitle>
          </DialogHeader>
          {previewLoading && (
            <div className="p-10 text-center text-sm text-[#525252]" style={{ height: "75vh" }}>Loading preview…</div>
          )}
          {!previewLoading && previewError && (
            <div className="p-6" style={{ height: "75vh" }}>
              <p className="text-sm font-bold text-red-600 mb-1">Could not load the proposal preview.</p>
              <p className="text-sm text-[#525252]">{previewError}</p>
            </div>
          )}
          {!previewLoading && !previewError && (
            <iframe
              srcDoc={previewHtml}
              className="w-full"
              style={{ height: "75vh" }}
              title="Proposal Preview"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Share-link dialog */}
      <Dialog open={shareDialog} onOpenChange={setShareDialog}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-lg p-0" data-testid="share-link-dialog">
          <DialogHeader className="p-5 border-b border-[#E5E5E5]">
            <DialogTitle className="font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <Share size={18} weight="bold" /> Public Share Link
            </DialogTitle>
          </DialogHeader>
          <div className="p-5 space-y-4">
            {shareLoading && <p className="text-sm text-[#525252]">Preparing link…</p>}
            {!shareLoading && shareInfo?.url && (
              <>
                <p className="text-xs text-[#525252] leading-relaxed">Anyone with this URL can view a read-only copy of proposal <span className="font-mono font-bold">{quote.quote_number}</span> — no sign-in required. Views are tracked. Revoke anytime.</p>
                <div className="flex items-center gap-2 border border-[#E5E5E5] bg-[#FAFAFA] p-2">
                  <input value={shareInfo.url} readOnly className="flex-1 bg-transparent text-xs font-mono text-[#0A0A0A] focus:outline-none" data-testid="share-link-input" />
                  <button onClick={copyShareLink} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECB]" data-testid="copy-share-link-btn"><Copy size={12} weight="bold" /> Copy</button>
                </div>
                {(shareInfo.view_count || shareInfo.view_count === 0) && (
                  <p className="text-[11px] text-[#A3A3A3]">
                    <span className="font-bold">{shareInfo.view_count || 0}</span> view{shareInfo.view_count === 1 ? "" : "s"}
                    {shareInfo.last_viewed_at && ` · last viewed ${formatLocal(shareInfo.last_viewed_at)}`}
                  </p>
                )}
                <div className="pt-3 border-t border-[#F5F5F5] flex justify-between">
                  <button onClick={revokeShareLink} disabled={shareLoading} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-600 text-xs font-bold uppercase tracking-wider hover:bg-red-50 disabled:opacity-50" data-testid="revoke-share-link-btn">
                    <LinkSimpleBreak size={12} weight="bold" /> Revoke Link
                  </button>
                  <button onClick={() => setShareDialog(false)} className="px-3 py-1.5 text-xs font-bold border border-[#E5E5E5] hover:bg-[#F5F5F5] uppercase tracking-wider">Done</button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Revision modal */}
      {showRevisionModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E5E5] w-full max-w-md p-5 space-y-4">
            <h3 className="font-bold text-sm text-[#0A0A0A] uppercase tracking-wider">Create Revision</h3>
            <p className="text-xs text-[#A3A3A3]">This creates a new draft copy (v{(quote.version_number || 1) + 1}) based on the current proposal.</p>
            <input value={revisionNote} onChange={e => setRevisionNote(e.target.value)} placeholder="Revision note (optional)" className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]" />
            <div className="flex gap-2">
              <button onClick={createRevision} disabled={creatingRevision} className="px-4 py-2 bg-[#0055FF] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50">{creatingRevision ? "Creating..." : "Create Revision"}</button>
              <button onClick={() => setShowRevisionModal(false)} className="px-4 py-2 text-sm font-bold border border-[#E5E5E5] hover:bg-[#F5F5F5] uppercase tracking-wider">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showSubmit && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" data-testid="submit-for-approval-modal">
          <div className="bg-white border border-[#E5E5E5] w-full max-w-md p-5 space-y-4">
            <h3 className="font-bold text-sm text-[#0A0A0A] uppercase tracking-wider">Submit for Approval</h3>
            <p className="text-xs text-[#A3A3A3]">A platform administrator will review and approve this proposal before it can be sent to the client. You'll get a notification either way.</p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoEmailOnApproval}
                onChange={e => setAutoEmailOnApproval(e.target.checked)}
                className="mt-1"
                data-testid="auto-email-on-approval-checkbox"
              />
              <span className="text-sm text-[#0A0A0A]">
                Automatically email the proposal to the client once approved
                <span className="block text-xs text-[#A3A3A3] mt-0.5">Otherwise you'll click "Email to Client" yourself after approval.</span>
              </span>
            </label>
            <div className="flex gap-2">
              <button onClick={submitForApproval} disabled={!!actionLoading} className="px-4 py-2 bg-[#D97706] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#B45309] disabled:opacity-50" data-testid="confirm-submit-approval-btn">
                {actionLoading === "submit-for-approval" ? "Submitting..." : "Submit"}
              </button>
              <button onClick={() => setShowSubmit(false)} className="px-4 py-2 text-sm font-bold border border-[#E5E5E5] hover:bg-[#F5F5F5] uppercase tracking-wider">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showReject && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" data-testid="reject-proposal-modal">
          <div className="bg-white border border-[#E5E5E5] w-full max-w-md p-5 space-y-4">
            <h3 className="font-bold text-sm text-[#0A0A0A] uppercase tracking-wider">Reject & Send Back</h3>
            <p className="text-xs text-[#A3A3A3]">The originator will be notified with this reason. They can edit and resubmit.</p>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason (e.g. 'Add 10% volume discount on annual line')" rows={4} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]" data-testid="reject-reason-textarea" />
            <div className="flex gap-2">
              <button onClick={rejectProposal} disabled={!!actionLoading} className="px-4 py-2 bg-[#DC2626] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#991B1B] disabled:opacity-50" data-testid="confirm-reject-btn">
                {actionLoading === "reject-approval" ? "Rejecting..." : "Reject & Notify"}
              </button>
              <button onClick={() => setShowReject(false)} className="px-4 py-2 text-sm font-bold border border-[#E5E5E5] hover:bg-[#F5F5F5] uppercase tracking-wider">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Approval status banner */}
      {pending && (
        <div className="mb-4 flex items-start gap-3 px-4 py-3 bg-[#FFFBEB] border border-[#FDE68A]" data-testid="approval-pending-banner">
          <Warning size={22} weight="fill" className="text-[#D97706] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-[#92400E]">Awaiting platform admin approval</p>
            <p className="text-xs text-[#B45309] mt-0.5">
              {isAdmin
                ? "You can edit before approving. Approve to release it to the client, or reject with a reason to send it back."
                : "A platform admin will review and approve your proposal. You'll be notified by email." }
              {quote.auto_email_on_approval && " On approval the proposal will be emailed to the client automatically."}
            </p>
          </div>
        </div>
      )}
      {approvalState === "rejected" && isOriginator && (
        <div className="mb-4 flex items-start gap-3 px-4 py-3 bg-[#FEF2F2] border border-[#FECACA]" data-testid="approval-rejected-banner">
          <XCircle size={22} weight="fill" className="text-[#DC2626] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-[#991B1B]">Proposal sent back for changes</p>
            {quote.approval_rejection_reason && <p className="text-xs text-[#B91C1C] mt-0.5 whitespace-pre-wrap">{quote.approval_rejection_reason}</p>}
            <p className="text-xs text-[#B91C1C] mt-1">Edit and resubmit to clear this notice.</p>
          </div>
        </div>
      )}
      {approvalState === "approved" && quote.status === "draft" && (
        <div className="mb-4 flex items-start gap-3 px-4 py-3 bg-[#F0FDF4] border border-[#BBF7D0]" data-testid="approval-approved-banner">
          <ShieldCheck size={22} weight="fill" className="text-[#16A34A] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-[#166534]">Approved — ready to send to client</p>
            <p className="text-xs text-[#15803D] mt-0.5">Click "Email to Client" to deliver.</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/quotes" className="inline-flex items-center gap-1.5 text-sm text-[#A3A3A3] hover:text-[#0A0A0A]"><ArrowLeft size={15} /> Back to Proposals</Link>
          {/* Proposal → Billing. A renewal proposal is an ordinary quote,
              so without this there is no way back to the commercial
              specification it was generated from. Renewals only. */}
          {data?.renewal_context && (
            <Link to={`/clients/${data.renewal_context.client_id}?tab=billing`}
                  className="inline-flex items-center gap-1.5 border border-[#E5E5E5] bg-white px-2 py-1 text-[11px] uppercase tracking-wider hover:border-[#0055FF] hover:text-[#0055FF]"
                  data-testid="proposal-to-billing">
              Renewal · {String(data.renewal_context.period_start || "").slice(0, 10)} → {String(data.renewal_context.period_end || "").slice(0, 10)} · Open client billing
            </Link>
          )}
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button onClick={openPreview} className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#0055FF] text-[#0055FF] text-sm font-bold uppercase tracking-wider hover:bg-[#F0F7FF]" data-testid="preview-quote-btn"><Eye size={14} weight="bold" /> Preview</button>
          <button onClick={downloadPdf} disabled={pdfLoading} className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#525252] text-[#525252] text-sm font-bold uppercase tracking-wider hover:bg-[#F5F5F5] disabled:opacity-50" data-testid="download-pdf-btn"><FilePdf size={14} weight="bold" /> {pdfLoading ? "Generating…" : "PDF"}</button>
          <button onClick={openShareDialog} className={`inline-flex items-center gap-2 px-3 py-1.5 border text-sm font-bold uppercase tracking-wider ${shareInfo ? "border-emerald-500 text-emerald-700 hover:bg-emerald-50" : "border-[#525252] text-[#525252] hover:bg-[#F5F5F5]"}`} data-testid="share-quote-btn"><Share size={14} weight="bold" /> {shareInfo ? "Share Link" : "Share"}</button>
          {/* Approval workflow buttons */}
          {needsApproval && !isAdmin && (
            <button onClick={() => setShowSubmit(true)} disabled={!!actionLoading} className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D97706] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#B45309] disabled:opacity-50" data-testid="submit-for-approval-btn">
              <ShieldCheck size={14} weight="bold" /> Submit for Approval
            </button>
          )}
          {pending && isAdmin && (
            <>
              <button onClick={approveProposal} disabled={!!actionLoading} className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#16A34A] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#15803D] disabled:opacity-50" data-testid="approve-proposal-btn">
                <CheckCircle size={14} weight="bold" /> Approve{quote.auto_email_on_approval ? " & Send" : ""}
              </button>
              <button onClick={() => setShowReject(true)} disabled={!!actionLoading} className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#DC2626] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#991B1B] disabled:opacity-50" data-testid="reject-proposal-btn">
                <XCircle size={14} weight="bold" /> Reject
              </button>
            </>
          )}
          {["draft", "in_rfp", "sent"].includes(quote.status) && canSendDirectly && !pending && (
            <button
              onClick={openSendConfirm}
              disabled={!!actionLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0055FF] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50"
              data-testid="send-quote-button"
              title={
                resolvedRecipient && resolvedRecipient.effectiveEmail
                  ? `Will be sent to: ${resolvedRecipient.effectiveName || "(no name)"} <${resolvedRecipient.effectiveEmail}>`
                    + (resolvedRecipient.mismatch
                       ? (resolvedRecipient.mismatch.ambiguous
                          ? `\n⚠ This name matches more than one contact (${resolvedRecipient.mismatch.greetedSources.join(" / ")}) with different emails. Reselect the intended contact below.`
                          : `\n⚠ This name matches the client's ${resolvedRecipient.mismatch.greetedSources[0]} contact, but the stored email belongs to their ${resolvedRecipient.mismatch.actualSources.join(" / ")} contact instead. Click below to fix.`)
                       : `\n(You can still change this in the next dialog before it sends.)`)
                  : "Recipient unresolved — open the dialog to pick one"
              }
            >
              <PaperPlaneTilt size={14} weight="bold" /> {quote.status === "sent" ? "Resend" : "Email to Client"}
            </button>
          )}
          {canEdit && <Link to={`/quotes/${quoteId}/edit`} className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E5E5E5] text-sm font-bold text-[#525252] hover:bg-[#F5F5F5] uppercase tracking-wider" data-testid="edit-quote-btn"><PencilSimple size={14} weight="bold" /> Edit</Link>}
          {canRevise && <button onClick={() => setShowRevisionModal(true)} className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E5E5E5] text-sm font-bold text-[#525252] hover:bg-[#F5F5F5] uppercase tracking-wider" data-testid="create-revision-btn"><GitBranch size={14} weight="bold" /> Revise</button>}
          <button onClick={copyProposal} className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E5E5E5] text-sm font-bold text-[#525252] hover:bg-[#F5F5F5] uppercase tracking-wider" data-testid="copy-proposal-btn" title="Create a fresh draft copy of this proposal"><Copy size={14} weight="bold" /> Copy</button>
        </div>
      </div>

      {/* Recipient mismatch warning (fix/proposal-recipient-contact-sync)
          — surfaces when the quote's stored recipient NAME exactly
          matches one or more known client contacts but the stored EMAIL
          belongs to a DIFFERENT known contact (greets one person, emails
          another). When the greeted name is AMBIGUOUS (shared by more
          than one role, e.g. Billing and Primary both "Theresa Munoz
          Wynn" with different emails), no one-click fix is offered —
          we cannot know which of the same-named contacts' emails was
          intended, so the operator must reselect via the chips below.
          An automatic fix is only offered when exactly one role explains
          the greeted name. Hidden on locked-status proposals where it
          can't be edited. */}
      {resolvedRecipient && resolvedRecipient.mismatch
       && ["draft", "in_rfp", "sent", "returned"].includes(quote.status)
       && canEdit && (
        <div className="bg-[#FFFBEB] border border-[#F59E0B] p-4 mb-4 flex items-start gap-3"
             data-testid="recipient-drift-banner">
          <Warning size={18} weight="duotone" className="text-[#B45309] mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-bold text-[#7C2D12]">
              This proposal greets
              {" "}
              <span className="font-mono">{resolvedRecipient.mismatch.greetedName}</span>
              {" "}({resolvedRecipient.mismatch.greetedSources.join(" / ")} contact{resolvedRecipient.mismatch.greetedSources.length > 1 ? "s" : ""}), but is set to email their
              {" "}
              <span className="font-bold">{resolvedRecipient.mismatch.actualSources.join(" / ")}</span> contact instead
              {" "}
              <span className="font-mono text-[12px]">&lt;{resolvedRecipient.mismatch.actualEmail}&gt;</span>.
            </p>
            <p className="text-[#92400E] text-xs mt-1">
              {resolvedRecipient.mismatch.ambiguous
                ? `"${resolvedRecipient.mismatch.greetedName}" matches more than one contact (${resolvedRecipient.mismatch.greetedSources.join(" and ")}) with different email addresses — an automatic fix isn't safe here. Select the intended Greet As contact using the chips below.`
                : `This proposal will greet "${resolvedRecipient.mismatch.greetedName.split(/\s+/)[0]}" by name but actually email a different contact's inbox. Reselect the intended Greet As contact, or click Fix to restore ${resolvedRecipient.mismatch.greetedName}'s own email address.`}
            </p>
          </div>
          {!resolvedRecipient.mismatch.ambiguous && resolvedRecipient.mismatch.fixRole && (
            <button
              onClick={applyRecommendedContact}
              disabled={applyingRecommended}
              className="shrink-0 h-8 px-3 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50 flex items-center gap-1"
              data-testid="apply-recommended-contact-btn"
            >
              {applyingRecommended ? "Updating…" : `Fix — use ${resolvedRecipient.mismatch.greetedSources[0]}'s email`}
            </button>
          )}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border border-[#E5E5E5] p-5 mb-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-black text-[#0A0A0A] tracking-tight font-mono">{quote.quote_number}</h1>
              <span className={`px-2 py-0.5 text-xs font-bold ${STATUS_COLORS[quote.status] || "bg-[#F5F5F5] text-[#525252]"}`}>{STATUS_LABEL[quote.status] || quote.status}</span>
              <span className="text-xs text-[#A3A3A3]">v{quote.version_number || 1}</span>
              <span className="px-2 py-0.5 text-[10px] font-bold tracking-[0.15em] uppercase bg-[#0055FF] text-white" data-testid="quote-currency-badge">Currency: {quote.currency_code}</span>
              {/* Proposal Type quick-switch — always visible, flips post-send with confirm */}
              <div
                className="inline-flex items-center bg-[#F8FAFC] border border-[#E5E5E5] p-0.5"
                data-testid="quote-type-switch"
                title="Switch proposal type"
              >
                <button
                  type="button"
                  onClick={() => switchQuoteType("new_customer")}
                  disabled={actionLoading === "switch-type"}
                  data-testid="quote-type-switch-new-customer"
                  className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider disabled:opacity-60 ${(quote.quote_type || "new_customer") !== "adhoc" ? "bg-white shadow-sm text-[#0055FF]" : "text-[#A3A3A3] hover:text-[#525252]"}`}
                >
                  <Buildings size={11} /> New Customer
                </button>
                <button
                  type="button"
                  onClick={() => switchQuoteType("adhoc")}
                  disabled={actionLoading === "switch-type"}
                  data-testid="quote-type-switch-adhoc"
                  className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider disabled:opacity-60 ${quote.quote_type === "adhoc" ? "bg-white shadow-sm text-[#0055FF]" : "text-[#A3A3A3] hover:text-[#525252]"}`}
                >
                  <UserCircle size={11} /> Ad-hoc
                </button>
              </div>
            </div>
            <p className="text-sm text-[#525252] mt-1">{recipientName}</p>
            {/* Session 91 iter-6/iter-7 — INLINE editable greeting name on the
                QuoteDetail screen.  Without this, Mike had to drop
                back into the Builder to change "Theresa" → "Lorna",
                and the Send dialog would default to whatever was
                saved.  Editable on draft/in_rfp only — saves
                immediately via PUT /api/quotes/{id}.  Iter-7: added
                PM / Billing / Primary picker chips so the operator
                can ONE-CLICK swap to a known client contact instead
                of re-typing the name. */}
            {(quote.status === "draft" || quote.status === "in_rfp") && (
              <div className="mt-1.5 space-y-1.5" data-testid="quote-greeting-name-editor">
                {/* Suggestion chips — built from data.client's PM /
                    billing / primary triples. Click to one-shot
                    populate both name inputs and persist via PUT. */}
                {(() => {
                  const c = client || {};
                  // fix/proposal-recipient-contact-sync — each chip now
                  // carries its OWN email + role alongside the name, so a
                  // click can update the full recipient identity (name +
                  // email) atomically in one request. Previously the chip
                  // only sent recipient_first_name/last_name, leaving
                  // billing_email pointed at whatever contact (often the
                  // PM) had been resolved earlier — Mike's Theresa/Lorna
                  // report.
                  const chips = [
                    { label: "PM", title: "Project Manager", role: "pm",
                      first: (c.pm_first_name||"").trim(), last: (c.pm_last_name||"").trim(),
                      email: (c.pm_contact_email||"").trim(), recommended: true },
                    { label: "Billing", title: "Billing contact", role: "billing",
                      first: (c.billing_first_name||"").trim(), last: (c.billing_last_name||"").trim(),
                      email: (c.billing_email||"").trim() },
                    { label: "Primary", title: "Primary contact", role: "primary",
                      first: (c.contact_first_name||"").trim(), last: (c.contact_last_name||"").trim(),
                      email: (c.contact_email||"").trim() },
                  ].filter(s => s.first || s.last);
                  if (chips.length === 0) return null;
                  return (
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="quote-greeting-chips">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-[#A3A3A3]">Greet as:</span>
                      {chips.map(s => {
                        const hasEmail = !!s.email;
                        const active = (quote.recipient_first_name||"").trim().toLowerCase() === s.first.toLowerCase()
                                       && (quote.recipient_last_name||"").trim().toLowerCase() === s.last.toLowerCase()
                                       && (quote.billing_email||"").trim().toLowerCase() === s.email.toLowerCase();
                        return (
                          <button
                            key={s.label}
                            type="button"
                            disabled={!hasEmail}
                            title={hasEmail
                              ? `${s.title} — ${[s.first, s.last].filter(Boolean).join(" ")} <${s.email}>`
                              : `${s.title} — ${[s.first, s.last].filter(Boolean).join(" ")} (no email on file)`}
                            data-testid={`quote-greeting-chip-${s.label.toLowerCase()}`}
                            onClick={async () => {
                              if (!hasEmail) {
                                // Never let a contact with no email on file
                                // silently inherit a different contact's
                                // address — block the pick and say why.
                                toast.error(`${s.title} has no email address on file. Add one to the client record or choose a different contact.`);
                                return;
                              }
                              try {
                                await api.put(`/quotes/${quote.quote_id}`, {
                                  recipient_first_name: s.first,
                                  recipient_last_name: s.last,
                                  billing_email: s.email,
                                  recipient_contact_role: s.role,
                                });
                                toast.success(`Greeting set to ${[s.first, s.last].filter(Boolean).join(" ")} (${s.title} — ${s.email})`);
                                load();
                              } catch (err) {
                                toast.error(extractErrorDetail(err?.response?.data?.detail, "Could not update greeting"));
                              }
                            }}
                            className={`h-6 px-2 text-[11px] font-bold border transition-colors flex items-center gap-1 ${
                              !hasEmail ? "bg-[#F5F5F5] text-[#A3A3A3] border-[#E5E5E5] cursor-not-allowed opacity-70"
                                     : active ? "bg-[#0055FF] text-white border-[#0055FF]"
                                     : s.recommended ? "bg-[#F0FDF4] text-[#16A34A] border-[#BBF7D0] hover:border-[#16A34A]"
                                                     : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0055FF]"
                            }`}
                          >
                            {s.recommended && !active && <span className="text-[8px] uppercase tracking-wider">★</span>}
                            <span>{[s.first, s.last].filter(Boolean).join(" ")}</span>
                            <span className={`text-[9px] font-normal uppercase tracking-wider ${active ? "opacity-70" : "opacity-60"}`}>· {s.label}</span>
                            {!hasEmail && <span className="text-[8px] uppercase tracking-wider">(no email)</span>}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
                {/* Free-text override — pick a chip OR over-type. */}
                <div className="inline-flex items-center gap-1.5 text-xs">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#A3A3A3]">Or type:</span>
                  <input
                    key={`first-${quote.recipient_first_name||""}`}
                    type="text"
                    defaultValue={quote.recipient_first_name || ""}
                    placeholder="First name (e.g. Lorna)"
                    data-testid="quote-greeting-first-name"
                    onBlur={async (e) => {
                      const newFirst = e.target.value.trim();
                      if (newFirst === (quote.recipient_first_name || "").trim()) return;
                      try {
                        await api.put(`/quotes/${quote.quote_id}`, { recipient_first_name: newFirst });
                        toast.success(`Greeting updated — proposal will address "${newFirst || "Hello"}"`);
                        load();
                      } catch (err) {
                        toast.error("Could not save greeting name");
                      }
                    }}
                    className="border border-[#E5E5E5] px-2 py-1 text-xs font-semibold text-[#0A0A0A] focus:outline-none focus:border-[#0055FF] w-32"
                  />
                  <input
                    key={`last-${quote.recipient_last_name||""}`}
                    type="text"
                    defaultValue={quote.recipient_last_name || ""}
                    placeholder="Last name"
                    data-testid="quote-greeting-last-name"
                    onBlur={async (e) => {
                      const newLast = e.target.value.trim();
                      if (newLast === (quote.recipient_last_name || "").trim()) return;
                      try {
                        await api.put(`/quotes/${quote.quote_id}`, { recipient_last_name: newLast });
                        toast.success("Greeting last-name updated");
                        load();
                      } catch (err) {
                        toast.error("Could not save greeting last-name");
                      }
                    }}
                    className="border border-[#E5E5E5] px-2 py-1 text-xs font-semibold text-[#0A0A0A] focus:outline-none focus:border-[#0055FF] w-32"
                  />
                </div>
                <p className="text-[10px] text-[#A3A3A3]">Shown on the printed PDF under "Prepared For" and as "Dear {(quote.recipient_first_name || "<first name>")}," on every email.</p>
              </div>
            )}
            {/* Session 90 — Email + phone shown inline on one row to
                save vertical space. When the backend resolved a
                linked lead_id, the whole row becomes one clickable
                hyperlink that opens the sales lead record in a new
                tab (so the operator can call from the displayed
                number, and click anywhere on the line to jump
                straight to the lead for note-taking, without leaving
                the proposal view). Tooltip surfaces the action. */}
            {(quote.billing_email || client?.contact_phone) && (
              client?.lead_id ? (
                <a
                  href={`/crm/leads/${client.lead_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open the sales lead record (new tab) to add notes"
                  data-testid="quote-recipient-block"
                  className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[#A3A3A3] hover:text-[#0055FF] hover:underline cursor-pointer mt-0.5"
                >
                  {quote.billing_email && <span>{quote.billing_email}</span>}
                  {quote.billing_email && client?.contact_phone && (
                    <span className="text-[#D4D4D4]">·</span>
                  )}
                  {client?.contact_phone && (
                    <span className="inline-flex items-center gap-1 font-mono">
                      <Phone size={11} weight="bold" />
                      {client.contact_phone}
                    </span>
                  )}
                </a>
              ) : (
                <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[#A3A3A3] mt-0.5">
                  {quote.billing_email && <span>{quote.billing_email}</span>}
                  {quote.billing_email && client?.contact_phone && (
                    <span className="text-[#D4D4D4]">·</span>
                  )}
                  {client?.contact_phone && (
                    <span className="inline-flex items-center gap-1 font-mono">
                      <Phone size={11} weight="bold" />
                      {client.contact_phone}
                    </span>
                  )}
                </div>
              )
            )}
          </div>
          <div className="text-right">
            <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold">Annual Total <span className="text-[#0055FF]">({quote.currency_code})</span></div>
            <div className="text-2xl font-black font-mono text-[#0055FF]">{fmt(annualTotal, quote.currency_code)}</div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-[#F5F5F5]">
          <div><div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold mb-0.5">Date</div><div className="text-sm font-mono">{quote.quote_date}</div></div>
          <div><div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold mb-0.5">Expiry</div><div className="text-sm font-mono">{quote.expiry_date || "-"}</div></div>
          <div><div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold mb-0.5">Currency</div><div className="text-sm">{quote.currency_code}</div></div>
          <div><div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold mb-0.5">Contract</div><div className="text-sm" data-testid="quote-contract-display">{quote.quote_type === "adhoc" ? <span className="text-[#A3A3A3] italic">N/A · Ad-hoc</span> : `${quote.contract_months || 36} months`}</div></div>
        </div>

        {/* DocuSign — per-proposal toggle. When ON, the customer's
            "Accept" click triggers a DocuSign envelope (status moves to
            `accepted_pending_signature`); when OFF the existing accept
            flow runs unchanged. Disabled once an envelope already
            exists — operator must void in DocuSign first if they need
            to back out. (Session 1.15.) */}
        {quote.status !== "rejected" && quote.status !== "accepted" && (
          <div className="mt-3 pt-3 border-t border-[#F5F5F5] flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs font-bold uppercase tracking-wider text-[#525252]">DocuSign signature on accept</p>
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 ${quote.use_docusign ? "bg-[#0055FF] text-white" : "bg-[#F5F5F5] text-[#A3A3A3]"}`}
                  data-testid="quote-docusign-state-badge"
                >
                  {quote.use_docusign ? "REQUIRED" : "OFF"}
                </span>
                {quote.docusign_envelope_id && (
                  <span
                    className="text-[10px] font-mono px-1.5 py-0.5 bg-[#FFFBEB] border border-[#F59E0B] text-[#92400E]"
                    title={`Envelope ID: ${quote.docusign_envelope_id}`}
                    data-testid="quote-docusign-envelope-badge"
                  >
                    Envelope: {(quote.docusign_status || "sent").toUpperCase()}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-[#A3A3A3] mt-1 leading-snug max-w-2xl">
                When ON, customer clicks Accept → quote moves to <em>Accepted (Pending Signature)</em> →
                a DocuSign envelope is auto-emailed → quote becomes Accepted only after they sign.
                When OFF, Accept goes straight to Accepted (existing flow).
              </p>
            </div>
            <button
              onClick={toggleDocusign}
              disabled={!!actionLoading || !!quote.docusign_envelope_id}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50 ${quote.use_docusign ? "bg-[#0055FF] text-white hover:bg-[#0044CC]" : "border border-[#E5E5E5] text-[#525252] hover:bg-[#F5F5F5]"}`}
              data-testid="quote-docusign-toggle"
              title={quote.docusign_envelope_id ? "An envelope already exists — void it in DocuSign first to back out" : ""}
            >
              {quote.use_docusign ? "Disable DocuSign" : "Require DocuSign"}
            </button>
          </div>
        )}

        {/* DocuSign admin smoke test — only surfaced when the toggle is
            ON, so we don't clutter the screen when DocuSign isn't in
            play for this proposal. */}
        {quote.use_docusign && !quote.docusign_envelope_id && (
          <div className="mt-2 text-right">
            <button
              onClick={sendDocusignTestEnvelope}
              disabled={!!actionLoading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#E5E5E5] text-[10px] font-bold uppercase tracking-wider text-[#525252] hover:bg-[#F5F5F5] disabled:opacity-50"
              data-testid="quote-docusign-test-envelope"
              title="Sends a test envelope to YOUR email — confirms the integration works without involving the customer"
            >
              Send Test Envelope (to me)
            </button>
          </div>
        )}

        {/* Status actions */}
        {quote.status === "sent" && (
          <div className="flex gap-2 mt-4 pt-4 border-t border-[#F5F5F5] flex-wrap">
            <button onClick={() => action("accept")} disabled={!!actionLoading} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50"><CheckCircle size={14} weight="bold" /> Accept</button>
            <button onClick={() => action("reject")} disabled={!!actionLoading} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-red-700 disabled:opacity-50"><XCircle size={14} weight="bold" /> Reject</button>
            <button onClick={() => action("mark-rfp")} disabled={!!actionLoading} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E5E5] text-xs font-bold text-[#525252] hover:bg-[#F5F5F5] uppercase tracking-wider disabled:opacity-50"><ClipboardText size={14} /> Mark RFP</button>
            <button
              onClick={sendReminderNow}
              disabled={!!actionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#0055FF] text-[#0055FF] text-xs font-bold uppercase tracking-wider hover:bg-[#F0F7FF] disabled:opacity-50"
              data-testid="send-reminder-now-btn"
              title={`Fire a manual reminder to ${quote.billing_email || "the proposal recipient"} now. The next automatic reminder will then fire 7 days from now.`}
            >
              <PaperPlaneTilt size={14} weight="bold" /> Send Reminder Now
            </button>
            <button
              onClick={openFollowup}
              disabled={!!actionLoading || fuLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#0055FF] text-[#0055FF] text-xs font-bold uppercase tracking-wider hover:bg-[#F0F7FF] disabled:opacity-50"
              data-testid="send-followup-btn"
              title="Compose a one-off follow-up email using the system-admin template. Pre-fills the customer's name, quote number, and sent date."
            >
              <EnvelopeSimple size={14} weight="bold" /> Send Follow-up
            </button>
            <button
              onClick={openAcceptOnBehalf}
              disabled={!!actionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-600 text-emerald-700 text-xs font-bold uppercase tracking-wider hover:bg-emerald-50 disabled:opacity-50"
              data-testid="accept-on-behalf-btn"
              title="Mark as accepted on behalf of the customer (use when they confirmed by email rather than clicking the Accept link)."
            >
              <CheckCircle size={14} weight="bold" /> Mark Accepted (Email)
            </button>
            <button
              onClick={openRejectOnBehalf}
              disabled={!!actionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-600 text-red-700 text-xs font-bold uppercase tracking-wider hover:bg-red-50 disabled:opacity-50"
              data-testid="reject-on-behalf-btn"
              title="Mark as rejected on behalf of the customer (use when they declined by email rather than clicking the Reject link)."
            >
              <XCircle size={14} weight="bold" /> Mark Rejected (Email)
            </button>
          </div>
        )}

        {/* Proposal Display Checkboxes sprint (2026-07-07) — three
            presentation-only toggles for the customer-facing proposal
            document (preview/PDF/email/public link — all rendered by the
            shared backend _proposal_html()). Placed alongside the FinOps
            action per spec, in one wrapping row. Editable while the
            proposal is still draft/in_rfp — the same edit window every
            other proposal field already uses (update_quote 400s outside
            it) — read-only afterward, same pattern as the existing
            show-year1-total toggle above. Never touches underlying
            pricing/quantity data, only what gets rendered. */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-4 pt-4 border-t border-[#F5F5F5]" data-testid="proposal-display-toggles-row">
          {[
            { key: "show_line_totals_horizontal", label: "Show Line Totals horizontally", testid: "show-line-totals" },
            { key: "show_subsection_totals", label: "Show Subsection Totals", testid: "show-subsection-totals" },
            { key: "show_document_total", label: "Document Total", testid: "show-document-total" },
            // Pre-existing Session 91 iter-5 field, relocated here (2026-07-07
            // follow-up) from its old spot down in the Grand Total card so
            // every proposal display control lives in one place.
            { key: "show_year1_total", label: "Show Total (Year 1) row on the printed proposal", testid: "show-year1-total-toggle" },
          ].map(({ key, label, testid }) => {
            const editable = quote.status === "draft" || quote.status === "in_rfp";
            const checked = quote[key] === undefined || quote[key] === null ? true : !!quote[key];
            return (
              <label
                key={key}
                className={`inline-flex items-center gap-2 text-xs text-[#525252] ${editable ? "cursor-pointer hover:text-[#0A0A0A]" : "opacity-60"}`}
                data-testid={`${testid}-label`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!editable}
                  onChange={async (e) => {
                    try {
                      await api.put(`/quotes/${quote.quote_id}`, { [key]: e.target.checked });
                      toast.success(`${label} ${e.target.checked ? "shown" : "hidden"} on the proposal`);
                      load();
                    } catch {
                      toast.error(`Could not update "${label}"`);
                    }
                  }}
                  className="accent-[#0055FF] disabled:cursor-not-allowed"
                  data-testid={`${testid}-checkbox`}
                />
                <span>{label}</span>
              </label>
            );
          })}

          {/* FinOps invoice action */}
          {["sent", "accepted"].includes(quote.status) && isAdmin && (
            <div className="flex items-center gap-2" data-testid="finops-invoice-row">
              {invoiceStatus?.has_invoice ? (
                <div className="flex items-center gap-2 text-xs">
                  <CheckCircle size={14} weight="bold" className="text-emerald-600" />
                  <span className="text-[#525252]">FinOps invoice</span>
                  <span className="font-mono font-bold text-[#0A0A0A]">{invoiceStatus.invoice_number}</span>
                  <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-[#F5F5F5] text-[#525252]">{invoiceStatus.status}</span>
                  <span className="text-[#A3A3A3]">· due {invoiceStatus.due_date}</span>
                </div>
              ) : (
                <button onClick={convertToInvoice} disabled={converting} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50" data-testid="convert-to-invoice-btn">
                  <FilePdf size={14} weight="bold" /> {converting ? "Creating…" : "Create Draft Invoice in FinOps"}
                </button>
              )}
            </div>
          )}
        </div>
        {quote.status === "draft" && (
          <div className="flex gap-2 mt-4 pt-4 border-t border-[#F5F5F5]">
            <button onClick={() => action("mark-rfp")} disabled={!!actionLoading} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E5E5] text-xs font-bold text-[#525252] hover:bg-[#F5F5F5] uppercase tracking-wider disabled:opacity-50"><ClipboardText size={14} /> Mark as In RFP</button>
          </div>
        )}

        {/* Archive / Delete */}
        <div className="flex gap-2 mt-4 pt-4 border-t border-[#F5F5F5]">
          {quote.status === "archived" ? (
            <button onClick={() => action("unarchive")} disabled={!!actionLoading} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#0055FF] text-[#0055FF] text-xs font-bold uppercase tracking-wider hover:bg-[#F0F7FF] disabled:opacity-50" data-testid="unarchive-quote-btn"><ArrowCounterClockwise size={14} weight="bold" /> Restore to Draft</button>
          ) : (
            <button onClick={() => action("archive")} disabled={!!actionLoading} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E5E5] text-xs font-bold text-[#525252] hover:bg-[#F5F5F5] uppercase tracking-wider disabled:opacity-50" data-testid="archive-quote-btn"><Archive size={14} /> Archive</button>
          )}
          <button onClick={async () => { if (!window.confirm(`Permanently delete proposal ${quote.quote_number}? This cannot be undone.`)) return; try { await api.delete(`/quotes/${quoteId}`); toast.success("Proposal deleted"); navigate("/quotes"); } catch { toast.error("Delete failed"); } }} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#DC2626] text-[#DC2626] text-xs font-bold uppercase tracking-wider hover:bg-[#FEF2F2] disabled:opacity-50" data-testid="delete-quote-btn"><Trash size={14} /> Delete</button>
        </div>
      </div>

      {/* Return-with-Comments thread + Reply panel */}
      {(quote.status === "returned" || threads.length > 0) && (
        <div className="bg-white border border-[#E5E5E5] mb-5 overflow-hidden" data-testid="comment-thread-section">
          <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center gap-2 bg-[#F0F7FF]">
            <ClipboardText size={14} className="text-[#0055FF]" />
            <h3 className="font-bold text-xs text-[#1E3A8A] uppercase tracking-wider">Return-With-Comments Thread</h3>
            <span className="ml-auto text-xs text-[#525252]">{threads.length} message{threads.length !== 1 ? "s" : ""}</span>
          </div>

          {quote.status === "returned" && (
            <div className="px-5 py-3 bg-[#FFFBEB] border-b border-[#FDE68A] flex items-start gap-2" data-testid="returned-banner">
              <Warning size={18} weight="fill" className="text-[#D97706] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#92400E] leading-relaxed">
                The client returned this proposal with comments and is awaiting your reply. Reminder emails are paused until you respond — sending a reply will flip the status back to <strong>Sent</strong> automatically.
              </p>
            </div>
          )}

          {threads.length > 0 && (
            <div className="divide-y divide-[#F5F5F5] max-h-96 overflow-y-auto" data-testid="comment-thread-list">
              {threads.map(t => {
                const isClient = t.direction === "client_to_sales";
                return (
                  <div key={t.thread_id} className={`px-5 py-3 ${isClient ? "bg-[#F0F7FF]/40" : "bg-white"}`} data-testid={`thread-row-${t.thread_id}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${isClient ? "bg-[#0055FF] text-white" : "bg-[#16A34A] text-white"}`}>
                        {isClient ? "Client" : "Eyvo"}
                      </span>
                      <span className="text-xs font-semibold text-[#0A0A0A]">{t.sender_name}</span>
                      {t.sender_email && <span className="text-[10px] text-[#A3A3A3] font-mono">{t.sender_email}</span>}
                      <span className="ml-auto text-[10px] text-[#A3A3A3]">{formatLocal(t.created_at)}</span>
                    </div>
                    <p className="text-sm text-[#0A0A0A] whitespace-pre-wrap leading-relaxed">{t.message}</p>
                    {t.attachment_name && (
                      <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-[#F5F5F5] text-[11px] text-[#525252]">
                        <FilePdf size={12} />
                        <span className="font-semibold">{t.attachment_name}</span>
                        {t.attachment_size && <span className="text-[#A3A3A3]">· {(t.attachment_size / 1024).toFixed(1)} KB</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Reply composer — only shown when status is 'returned' or for currently-active proposals */}
          {["sent", "returned", "in_rfp"].includes(quote.status) && (
            <div className="px-5 py-4 border-t border-[#E5E5E5] bg-[#FAFAFA]" data-testid="reply-composer">
              <label className="block text-[10px] text-[#525252] uppercase tracking-wider font-bold mb-1.5">
                Reply to Client
              </label>
              <textarea
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                rows={4}
                placeholder="Type your response — this will be emailed to the client and the proposal will flip back to 'Sent'."
                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF] bg-white"
                data-testid="reply-textarea"
              />
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E5E5] text-xs font-bold text-[#525252] hover:bg-white cursor-pointer uppercase tracking-wider" data-testid="reply-attachment-label">
                  <FilePdf size={14} />
                  {replyFile ? replyFile.name.slice(0, 32) : "Attach file"}
                  <input
                    type="file"
                    onChange={e => setReplyFile(e.target.files?.[0] || null)}
                    className="hidden"
                    data-testid="reply-attachment-input"
                  />
                </label>
                {replyFile && (
                  <button onClick={() => setReplyFile(null)} className="text-xs text-[#A3A3A3] underline hover:text-[#DC2626]" data-testid="clear-attachment-btn">
                    Clear
                  </button>
                )}
                <button
                  onClick={sendReply}
                  disabled={sendingReply || replyText.trim().length < 2}
                  className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50"
                  data-testid="send-reply-btn"
                >
                  <PaperPlaneTilt size={14} weight="bold" />
                  {sendingReply ? "Sending…" : "Send Reply"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}



      {/* Version History */}
      {versions.length > 1 && (
        <div className="bg-white border border-[#E5E5E5] mb-5 overflow-hidden" data-testid="version-history">
          <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center gap-2">
            <GitBranch size={14} className="text-[#0055FF]" />
            <h3 className="font-bold text-xs text-[#525252] uppercase tracking-wider">Version History</h3>
            <span className="ml-auto text-xs text-[#A3A3A3]">{versions.length} versions</span>
          </div>
          <div className="divide-y divide-[#F5F5F5]">
            {[...versions].reverse().map(v => {
              const isCurrent = v.quote_id === quoteId;
              return (
                <div key={v.quote_id} className={`px-5 py-3 ${isCurrent ? "bg-blue-50/50" : ""}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 flex items-center justify-center text-xs font-bold flex-shrink-0 ${isCurrent ? "bg-[#0055FF] text-white" : "bg-[#F5F5F5] text-[#525252]"}`}>v{v.version_number}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {isCurrent ? <span className="text-xs font-bold text-[#0055FF]">Current (v{v.version_number})</span> : <Link to={`/quotes/${v.quote_id}`} className="text-xs text-[#525252] hover:text-[#0055FF] underline">View v{v.version_number}</Link>}
                        <span className={`px-1.5 py-0.5 text-[10px] font-bold ${STATUS_COLORS[v.status] || ""}`}>{STATUS_LABEL[v.status] || v.status}</span>
                        {v.version_note && <span className="text-xs text-[#A3A3A3] italic truncate max-w-[200px]">{v.version_note}</span>}
                      </div>
                    </div>
                    <span className="text-xs font-mono text-[#A3A3A3]" title={`Year-1 Total — recomputed from line items.\n(Stored total_amount = ${fmt(v.total_amount, v.currency_code)})`}>{fmt(v.display_total ?? v.total_amount, v.currency_code)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Line Items */}
      {SECTIONS.map(section => {
        const items = line_items.filter(l => (l.category_snapshot || "user_licenses") === section.key);
        if (items.length === 0) return null;
        const secMo = items.reduce((s, l) => s + lineMo(l), 0);
        const secYr = items.reduce((s, l) => s + lineYr(l), 0);
        const secOO = items.reduce((s, l) => s + lineOO(l), 0);
        return (
          <div key={section.key} className="bg-white border border-[#E5E5E5] mb-3 overflow-hidden">
            <div className={`px-4 py-2.5 ${section.headerCls}`}><h3 className="text-xs font-bold uppercase tracking-wider">{section.label}</h3></div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#E5E5E5]">
                <th className="text-left px-4 py-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider">Item</th>
                <th className="text-right px-4 py-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-[84px]">Qty</th>
                <th className="text-right px-4 py-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-24">Price</th>
                <th className="text-right px-4 py-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-24">/Month</th>
                <th className="text-right px-4 py-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-24">/Year</th>
              </tr></thead>
              <tbody className="divide-y divide-[#F5F5F5]">
                {items.map((item, i) => {
                  const addon = Number(item.monthly_addon_unit_price) || 0;
                  const addonTotal = lineAddonMo(item);
                  return (
                  <tr key={i}>
                    <td className="px-4 py-2.5">
                      <span className="font-semibold text-[#0A0A0A]">{item.description_snapshot}</span>
                      {item.product_code_snapshot && <span className="ml-2 text-[10px] text-[#A3A3A3] font-mono">{item.product_code_snapshot}</span>}
                      {round2(addon) !== 0 && (
                        <span className="ml-2 text-[11px] text-[#525252] font-normal" title="Recurring monthly fee for this line (flat — not multiplied by qty). Included in the Recurring Fees subtotal below.">
                          (+ {fmt(addon, quote.currency_code)}/mo ongoing)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#525252]">{item.quantity}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#525252]">{fmt(item.unit_price, quote.currency_code)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#525252]">{round2(lineMo(item)) !== 0 ? fmt(lineMo(item), quote.currency_code) : "-"}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-[#0A0A0A]">{(item.pricing_type_snapshot || "annual") === "one_off" ? fmt(lineOO(item), quote.currency_code) : fmt(lineYr(item), quote.currency_code)}</td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-[#E5E5E5] bg-[#FAFAFA]">
                  <td className="px-4 py-2 font-bold text-[10px] text-[#525252] uppercase tracking-wider" colSpan={3}>
                    Section Subtotal
                    {round2(secOO) !== 0 && (
                      <div className="font-normal text-[11px] normal-case tracking-normal text-[#525252] mt-0.5">
                        One-Off Fees: <span className="font-mono font-semibold text-[#0A0A0A]">{fmt(secOO, quote.currency_code)}</span>
                      </div>
                    )}
                    {round2(secMo) !== 0 && (
                      <div className="font-normal text-[11px] normal-case tracking-normal text-[#525252] mt-0.5">
                        Recurring Fees: <span className="font-mono font-semibold text-[#0A0A0A]">{fmt(secMo, quote.currency_code)}/mo</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono font-semibold text-xs align-top">{round2(secMo) !== 0 ? fmt(secMo, quote.currency_code) : "-"}</td>
                  <td className="px-4 py-2 text-right font-mono font-bold text-xs align-top">{fmt(secYr + secOO, quote.currency_code)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })}

      {/* Grand total */}
      <div className="bg-white border border-[#E5E5E5] p-5 mb-5">
        <div className="flex justify-end">
          <div className="w-72 space-y-1.5 text-sm">
            {round2(totalMo) !== 0 && <div className="flex justify-between text-[#525252]"><span>Monthly</span><span className="font-mono">{fmt(totalMo, quote.currency_code)}/mo</span></div>}
            {round2(totalOO) !== 0 && <div className="flex justify-between text-[#525252]"><span>One-off</span><span className="font-mono">{fmt(totalOO, quote.currency_code)}</span></div>}
            {(quote.discount_amount > 0 || quote.tax_amount > 0) && <div className="border-t border-[#F5F5F5] pt-1.5 space-y-1">
              {round2(quote.discount_amount) !== 0 && <div className="flex justify-between text-[#A3A3A3] text-xs"><span>Discount</span><span className="font-mono text-red-600">{fmt(-Number(quote.discount_amount || 0), quote.currency_code)}</span></div>}
              {round2(quote.tax_amount) !== 0 && <div className="flex justify-between text-[#A3A3A3] text-xs"><span>Tax</span><span className="font-mono">{fmt(quote.tax_amount, quote.currency_code)}</span></div>}
            </div>}
            {/* Session 91 iter-5 / 2026-07-07 relocation follow-up — the
                editable checkbox for `show_year1_total` now lives up in
                the "Proposal Display" row next to the other display
                toggles (near the FinOps action), not here. This is just
                the read-only total figure, gated by the SAME combined
                condition _proposal_html() uses server-side (document
                total master switch AND the year-1-specific refinement)
                so this staff-facing card never shows a total the
                customer-facing document has hidden. */}
            {(
              (quote.show_document_total === undefined || quote.show_document_total === null || quote.show_document_total) &&
              (quote.show_year1_total === undefined || quote.show_year1_total === null || quote.show_year1_total)
            ) && (
              <div className="flex justify-between items-end pt-2 border-t-2 border-[#E5E5E5]" data-testid="year1-total-row">
                <div className="font-black text-[#0A0A0A]">Year-1 Total</div>
                <span className="font-mono font-black text-xl text-[#0055FF]" title={`Year-1 = ${round2(totalMo) !== 0 ? `${fmt(totalMo, quote.currency_code)}/mo × 12 + ` : ''}${round2(totalYr) !== 0 ? `Annual ${fmt(totalYr, quote.currency_code)} + ` : ''}${round2(totalOO) !== 0 ? `One-off ${fmt(totalOO, quote.currency_code)}` : ''}${round2(quote.discount_amount) !== 0 ? ` − Discount ${fmt(quote.discount_amount, quote.currency_code)}` : ''}${round2(quote.tax_amount) !== 0 ? ` + Tax ${fmt(quote.tax_amount, quote.currency_code)}` : ''}`}>{fmt(annualTotal, quote.currency_code)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notes */}
      {(quote.notes_internal || quote.notes_external || quote.standard_notes) && (
        <div className="bg-white border border-[#E5E5E5] p-5 mb-5 space-y-4">
          {quote.notes_external && <div><div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold mb-1">External Notes</div><p className="text-sm text-[#525252] whitespace-pre-wrap">{quote.notes_external}</p></div>}
          {quote.notes_internal && <div><div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold mb-1">Internal Notes</div><p className="text-sm text-[#525252] whitespace-pre-wrap">{quote.notes_internal}</p></div>}
          {quote.standard_notes && <div><div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold mb-1">Standard Notes</div><p className="text-xs text-[#A3A3A3] whitespace-pre-wrap font-mono">{quote.standard_notes}</p></div>}
        </div>
      )}

      {/* Email log */}
      {emailLog.length > 0 && (
        <div className="bg-white border border-[#E5E5E5] overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E5E5E5]"><h3 className="font-bold text-xs text-[#525252] uppercase tracking-wider">Email Log</h3></div>
          <div className="divide-y divide-[#F5F5F5]">
            {emailLog.map(log => (
              <div key={log.log_id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#525252]">{log.to_email}</p>
                  <p className="text-[10px] text-[#A3A3A3]">{log.sent_at ? formatLocal(log.sent_at) : "-"}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs text-[#A3A3A3]">{log.open_count || 0} opens</span>
                  {log.first_opened_at && <p className="text-[10px] text-emerald-600">First opened: {formatLocal(log.first_opened_at)}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Send-confirmation dialog — recipient override + optional CC */}
      {sendConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSendConfirm(null)} data-testid="send-confirm-backdrop">
          <div className="bg-white border border-[#E5E5E5] max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="send-confirm-dialog">
            <h3 className="text-base font-bold text-[#0A0A0A] mb-1 flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <PaperPlaneTilt size={16} weight="bold" /> {sendConfirm.type === "resend" ? "Resend proposal" : "Send proposal"} {quote.quote_number}
            </h3>
            <p className="text-xs text-[#525252] mb-4">Review the recipient name, email and optional CC addresses before sending. You can override the To: name and address for this send only — the proposal record will be updated to match.</p>
            <label className="block text-[10px] font-bold text-[#525252] uppercase tracking-wider mb-1">Recipient name</label>
            <input
              type="text"
              value={sendConfirm.name || ""}
              onChange={e => setSendConfirm(s => ({ ...s, name: e.target.value }))}
              className="w-full rounded-none border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
              placeholder="e.g. Lorna Cisneros"
              data-testid="send-confirm-name-input"
            />
            {(sendConfirm.name || "").trim() !== (sendConfirm.originalName || "").trim() && (
              <p className="mt-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1.5" data-testid="send-confirm-name-changed">
                Changed from <strong>{sendConfirm.originalName || "(none)"}</strong>. This name will be used in the greeting and the To: display.
              </p>
            )}
            <label className="block text-[10px] font-bold text-[#525252] uppercase tracking-wider mt-4 mb-1 flex items-center gap-1.5">
              Recipient email
              <span
                className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#0055FF] text-white text-[8px] font-bold cursor-help normal-case tracking-normal"
                title="We confirm the recipient before every send. Edit this address if needed — nothing leaves Eyvo until you click Send."
                data-testid="send-confirm-email-tooltip"
                aria-label="The system will not send blindly — it confirms this address first"
              >i</span>
              <span className="text-[10px] text-[#A3A3A3] normal-case tracking-normal font-normal">we confirm before sending</span>
            </label>
            <input
              type="email"
              value={sendConfirm.to}
              onChange={e => setSendConfirm(s => ({ ...s, to: e.target.value }))}
              className="w-full rounded-none border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
              title="We confirm the recipient before every send. Edit this address if needed — nothing leaves Eyvo until you click Send."
              data-testid="send-confirm-email-input"
            />
            {sendConfirm.source && (
              <p className="mt-1 text-[11px] text-[#525252]" data-testid="send-confirm-source">
                Pre-populated from <strong>{
                  sendConfirm.source === "pm" ? "PM contact" :
                  sendConfirm.source === "billing" ? "Billing contact" :
                  sendConfirm.source === "contact" ? "Main contact" : sendConfirm.source
                }</strong>.
              </p>
            )}
            {(sendConfirm.to || "").trim().toLowerCase() !== (sendConfirm.original || "").toLowerCase() && (
              <p className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1.5">
                Changed from <strong>{sendConfirm.original || "(none)"}</strong>. The proposal's billing email will be updated to this address.
              </p>
            )}
            <label className="block text-[10px] font-bold text-[#525252] uppercase tracking-wider mt-4 mb-1">CC <span className="text-[#A3A3A3] normal-case font-normal">(optional, comma or semicolon separated)</span></label>
            <input
              type="text"
              value={sendConfirm.cc}
              onChange={e => setSendConfirm(s => ({ ...s, cc: e.target.value }))}
              className="w-full rounded-none border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
              placeholder="cc1@example.com, cc2@example.com"
              data-testid="send-confirm-cc-input"
            />
            {/* Send-now vs Schedule-for-later toggle (Session 67).
                Only offered for the FIRST send — resends always go now. */}
            {sendConfirm.type !== "resend" && (
              <div className="mt-4 pt-4 border-t border-[#F5F5F5]">
                <div className="flex gap-2 mb-2" role="tablist">
                  <button
                    type="button"
                    onClick={() => setSendConfirm(s => ({ ...s, scheduleMode: "now", scheduledAt: "" }))}
                    className={`flex-1 h-8 text-[11px] font-bold uppercase tracking-wider border ${
                      (sendConfirm.scheduleMode || "now") === "now"
                        ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                        : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0055FF]"
                    }`}
                    data-testid="send-mode-now"
                  >
                    Send now
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendConfirm(s => ({ ...s, scheduleMode: "later" }))}
                    className={`flex-1 h-8 text-[11px] font-bold uppercase tracking-wider border ${
                      sendConfirm.scheduleMode === "later"
                        ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                        : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0055FF]"
                    }`}
                    data-testid="send-mode-later"
                  >
                    Schedule for later
                  </button>
                </div>
                {sendConfirm.scheduleMode === "later" && (
                  <>
                    <label className="block text-[10px] font-bold text-[#525252] uppercase tracking-wider mb-1">Send at</label>
                    <input
                      type="datetime-local"
                      value={sendConfirm.scheduledAt || ""}
                      onChange={e => setSendConfirm(s => ({ ...s, scheduledAt: e.target.value }))}
                      min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                      className="w-full rounded-none border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF] font-mono"
                      data-testid="send-confirm-scheduled-at"
                    />
                    <p className="text-[10px] text-[#A3A3A3] mt-1">
                      The proposal will be sent automatically at this time. Recipient name + email + CC are saved on the proposal now and used at send time.
                      {quote.status === "pending_approval" && (
                        <span className="block mt-1 text-amber-800">
                          Pending approval — the dispatcher will hold the schedule until a platform admin approves.
                        </span>
                      )}
                    </p>
                  </>
                )}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setSendConfirm(null)} className="px-3 py-1.5 border border-[#E5E5E5] text-sm font-bold text-[#525252] hover:bg-[#F5F5F5] uppercase tracking-wider" data-testid="send-confirm-cancel">Cancel</button>
              <button onClick={confirmSend} disabled={actionLoading === "send" || (sendConfirm.scheduleMode === "later" && !sendConfirm.scheduledAt)} className="px-4 py-1.5 bg-[#0055FF] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50" data-testid="send-confirm-submit">
                {actionLoading === "send"
                  ? (sendConfirm.scheduleMode === "later" ? "Scheduling…" : "Sending proposal…")
                  : sendConfirm.scheduleMode === "later"
                    ? "Schedule"
                    : (sendConfirm.type === "resend" ? "Resend Now" : "Send Now")}
              </button>
            </div>
            {actionLoading === "send" && sendSlowNotice && sendConfirm.scheduleMode !== "later" && (
              <p className="mt-2 text-right text-[11px] text-amber-700" data-testid="send-slow-notice">
                Still sending — this can take a moment.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Session 88 — Accept on behalf (customer replied by email) */}
      <Dialog open={showAcceptOnBehalf} onOpenChange={setShowAcceptOnBehalf}>
        <DialogContent className="max-w-lg" data-testid="aob-dialog">
          <DialogHeader>
            <DialogTitle>Mark accepted (received via email)</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#737373] -mt-2 mb-3">
            Records the customer as the named acceptor with the pasted email text saved on the proposal + in the audit log.
            Pauses all further reminders.
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#525252] block mb-1">Client contact email</label>
              <input type="email" value={aobEmail} onChange={e => setAobEmail(e.target.value)}
                placeholder="e.g. jane.smith@acme.com"
                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
                data-testid="aob-email-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#525252] block mb-1">Client contact name <span className="text-[#A3A3A3] font-normal lowercase">(optional)</span></label>
              <input type="text" value={aobName} onChange={e => setAobName(e.target.value)}
                placeholder="e.g. Jane Smith"
                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
                data-testid="aob-name-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#525252] block mb-1">Evidence — paste their email body</label>
              <textarea value={aobEvidence} onChange={e => setAobEvidence(e.target.value)}
                placeholder='Paste the relevant part of the customer&apos;s email, e.g. "We&apos;re happy to proceed with the quote — please send the contract."'
                rows={8}
                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#0055FF]"
                data-testid="aob-evidence-textarea" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowAcceptOnBehalf(false)} className="px-4 py-2 text-sm font-bold border border-[#E5E5E5] hover:bg-[#F5F5F5] uppercase tracking-wider">Cancel</button>
              <button onClick={submitAcceptOnBehalf} disabled={actionLoading === "accept-on-behalf"}
                className="px-4 py-2 text-sm font-bold bg-emerald-600 text-white uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50"
                data-testid="aob-confirm-btn">
                {actionLoading === "accept-on-behalf" ? "Saving…" : "Mark as accepted"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Session 88 — Reject on behalf (customer declined by email) */}
      <Dialog open={showRejectOnBehalf} onOpenChange={setShowRejectOnBehalf}>
        <DialogContent className="max-w-lg" data-testid="rob-dialog">
          <DialogHeader>
            <DialogTitle>Mark rejected (received via email)</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#737373] -mt-2 mb-3">
            Records the customer as the named decliner with the pasted email text saved on the proposal + in the audit log.
            Pauses all further reminders.
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#525252] block mb-1">Client contact email</label>
              <input type="email" value={robEmail} onChange={e => setRobEmail(e.target.value)}
                placeholder="e.g. jane.smith@acme.com"
                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
                data-testid="rob-email-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#525252] block mb-1">Client contact name <span className="text-[#A3A3A3] font-normal lowercase">(optional)</span></label>
              <input type="text" value={robName} onChange={e => setRobName(e.target.value)}
                placeholder="e.g. Jane Smith"
                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
                data-testid="rob-name-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#525252] block mb-1">Evidence — paste their email body</label>
              <textarea value={robEvidence} onChange={e => setRobEvidence(e.target.value)}
                placeholder='Paste the relevant part of the customer&apos;s email, e.g. "Thanks for the quote — we&apos;ve decided to go with another vendor."'
                rows={8}
                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#0055FF]"
                data-testid="rob-evidence-textarea" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowRejectOnBehalf(false)} className="px-4 py-2 text-sm font-bold border border-[#E5E5E5] hover:bg-[#F5F5F5] uppercase tracking-wider">Cancel</button>
              <button onClick={submitRejectOnBehalf} disabled={actionLoading === "reject-on-behalf"}
                className="px-4 py-2 text-sm font-bold bg-red-600 text-white uppercase tracking-wider hover:bg-red-700 disabled:opacity-50"
                data-testid="rob-confirm-btn">
                {actionLoading === "reject-on-behalf" ? "Saving…" : "Mark as rejected"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Session 88 — Send follow-up email */}
      <Dialog open={showFollowup} onOpenChange={setShowFollowup}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="followup-dialog">
          <DialogHeader>
            <DialogTitle>Send follow-up email</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#737373] -mt-2 mb-3">
            Pre-populated from the System Admin template. Edit anything below; the
            email is wrapped in the standard Eyvo template at send time so it looks
            identical to every other email from the system.
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#525252] block mb-1">To</label>
              <input type="email" value={fuTo} onChange={e => setFuTo(e.target.value)}
                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
                data-testid="followup-to-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#525252] block mb-1">Subject</label>
              <input type="text" value={fuSubject} onChange={e => setFuSubject(e.target.value)}
                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
                data-testid="followup-subject-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#525252] block mb-1">Body</label>
              <RichTextEditor
                html={fuBodyHtml}
                onChange={setFuBodyHtml}
                placeholder="Write your follow-up — fonts, colours, links and signatures all work."
                minHeight={280}
              />
            </div>
            <div className="flex justify-between items-center gap-2 pt-2">
              <Link to="/admin/email-templates" className="text-[11px] uppercase tracking-wider text-[#525252] hover:text-[#0055FF]" target="_blank">
                Edit default template →
              </Link>
              <div className="flex gap-2">
                <button onClick={() => setShowFollowup(false)} className="px-4 py-2 text-sm font-bold border border-[#E5E5E5] hover:bg-[#F5F5F5] uppercase tracking-wider">Cancel</button>
                <button onClick={submitFollowup} disabled={actionLoading === "send-followup"}
                  className="px-4 py-2 text-sm font-bold bg-[#0055FF] text-white uppercase tracking-wider hover:bg-[#0044CC] disabled:opacity-50"
                  data-testid="followup-send-btn">
                  {actionLoading === "send-followup" ? "Sending…" : "Send follow-up"}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
